/**
 * 세션 유휴 로그아웃 훅 — 전역 보안정책.
 *
 * 전역 보안정책의 세션 타임아웃(공개 config, 예: "30분")을 읽어, 사용자 무활동 N분 후 자동 로그아웃함. 세션 유휴는
 * Keycloak realm ssoSessionIdleTimeout 이 FE 토큰 자동갱신(auth.ts kcRefreshTimer)에 무력화되므로 앱 타이머가 집행 권위임.
 * 이 훅은 유휴 감지 타이머만 관리하고, 만료 시 logout() 이 토큰·갱신타이머를 정리함.
 */
import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

import { logout } from "@/lib/auth";
import { useSecurityPolicyPublic } from "@/lib/queries/useSecurityPolicy";

/** 세션 설정값("15분")에서 분(minutes)을 파싱. 숫자가 없으면 null. */
export function parseSessionMinutes(value: string | undefined): number | null {
  if (!value) return null;
  const m = /(\d+)/.exec(value);
  if (!m) return null;
  const n = Number(m[1]);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/** 세션 설정 미지정 시 기본 유휴 분. 보안정책 화면(SecurityPolicyPage)의 기본 표시값도 이 값을 씀. */
export const DEFAULT_IDLE_MIN = 15;

/** 유휴 판정을 리셋하는 사용자 활동 이벤트. */
const ACTIVITY_EVENTS: readonly string[] = ["mousedown", "keydown", "scroll", "touchstart"];

/**
 * 현재 워크스페이스의 세션 타임아웃을 적용해 유휴 시 자동 로그아웃함. AppShell(워크스페이스 컨텍스트)에서 1회 호출함.
 *
 * @param slug 현재 워크스페이스 slug. 없으면(비-워크스페이스 컨텍스트) 동작하지 않음.
 */
export function useIdleLogout(slug: string | undefined): void {
  const navigate = useNavigate();
  const { data: policy } = useSecurityPolicyPublic();
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const idleMin = parseSessionMinutes(policy?.session ?? undefined) ?? DEFAULT_IDLE_MIN;

  useEffect(() => {
    if (!slug) return;
    const idleMs = idleMin * 60 * 1000;

    const reset = () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        // 유휴 만료 → 세션 종료. logout() 이 토큰·자동갱신 타이머를 정리함.
        void logout().finally(() => navigate("/login", { replace: true }));
      }, idleMs);
    };

    reset();
    for (const ev of ACTIVITY_EVENTS) {
      window.addEventListener(ev, reset, { passive: true });
    }
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      for (const ev of ACTIVITY_EVENTS) {
        window.removeEventListener(ev, reset);
      }
    };
  }, [slug, idleMin, navigate]);
}
